import { useEffect, useState } from 'react';
import type {
  BedConfig,
  BedType,
  Room,
  RoomBed,
  RoomConfigPatch,
  RoomType,
  BedSpace,
} from '@lemontaigu/pms-core';
import { UNIT_KIND_LABELS_FR } from '@lemontaigu/pms-core';
import { BedIcons } from './RoomIcons.js';
import { SpaceEditor, UnitScalars } from './UnitFields.js';
import { bedLayoutFor, bedLayoutLookup, fmtDate } from './logic.js';

// « Fiche chambre » — the room-configuration editor (room-edit brief E4).
//
// A panel rather than inline cells: the table is twelve columns, four of them
// pictograms, and editing in place would wreck the reading it exists for. The
// panel is also where per-room content (photos, notes) would land later.
//
// It edits a LOCAL draft and submits the whole row in one call, because the RPC
// is whole-row: a partial patch would blank what it omits. Cancel throws the
// draft away; nothing is written until Enregistrer.
//
// Read-only without the capability — the L1 gate. Cosmetic by design: the RPC
// refuses regardless (ADR-0013, one gate per layer).

const VIEWS = ['Ville', 'Château', 'Jardin'] as const;
const ZONES = [1, 2, 3] as const;

export interface RoomSheetProps {
  room: Room;
  roomTypes: RoomType[];
  bedConfigs: BedConfig[];
  bedTypes: BedType[];
  /** This unit's per-space beds — empty for every single-space unit, which is
   *  every hotel room. */
  beds: RoomBed[];
  canEdit: boolean;
  saving: boolean;
  error: string;
  onSave: (patch: RoomConfigPatch) => void;
  onDelete: () => void;
  onClose: () => void;
}

const toPatch = (r: Room, beds: RoomBed[], roomTypes: RoomType[]): RoomConfigPatch => ({
  roomTypeId: r.roomTypeId,
  configOptions: [...r.configOptions],
  doubleBedType: r.doubleBedType,
  maxOccupancy: r.maxOccupancy ?? 1,
  isSuperior: r.isSuperior,
  heatingZone: r.heatingZone,
  bathroomHeatingZone: r.bathroomHeatingZone,
  view: r.view,
  hasShower: r.hasShower,
  wcNotSeparated: r.wcNotSeparated,
  remarks: r.remarks,
  unit: {
    unitKind: r.unitKind,
    bedrooms: r.bedrooms,
    livingRooms: r.livingRooms,
    bathrooms: r.bathrooms,
    wcCount: r.wcCount,
    kitchen: r.kitchen,
    surfaceSqm: r.surfaceSqm,
  },
  beds,
  // Seeded from the room's CURRENT category so `dirty` can see a relabel, and
  // so the field renders what is really stored rather than an empty box. It is
  // sent only for an apartment-like unit (see `submit`) — a hotel room's
  // category is shared and the RPC refuses to edit a shared one.
  type: (() => {
    const t = roomTypes.find((x) => x.id === r.roomTypeId);
    return t ? { labelFr: t.labelFr, maxOccupancy: t.maxOccupancy } : null;
  })(),
});

export function RoomSheet({
  room,
  roomTypes,
  bedConfigs,
  bedTypes,
  beds,
  canEdit,
  saving,
  error,
  onSave,
  onDelete,
  onClose,
}: RoomSheetProps) {
  const [draft, setDraft] = useState<RoomConfigPatch>(() => toPatch(room, beds, roomTypes));
  // Deleting is rare and irreversible, so it asks once. The DB refuses anyway
  // if anything references the room (brief E5) — this only stops a slip.
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Switching rooms without closing the panel must reload the form, not keep
  // the previous room's draft.
  useEffect(() => {
    setDraft(toPatch(room, beds, roomTypes));
    setConfirmDelete(false);
  }, [room, beds, roomTypes]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (patch: Partial<RoomConfigPatch>) => setDraft((d) => ({ ...d, ...patch }));
  const lookup = bedLayoutLookup(bedConfigs);
  const chosen = draft.configOptions ?? [];
  const preview = chosen.map((code) => ({ code, layout: bedLayoutFor(lookup, code) }));
  /** A double is only a question when one of the chosen arrangements has one. */
  const hasDouble = preview.some((p) => (p.layout?.doubles ?? 0) > 0);
  const toggleOption = (code: string) =>
    set({
      configOptions: chosen.includes(code)
        ? chosen.filter((c) => c !== code)
        : [...chosen, code].sort((a, b) => a.localeCompare(b, 'fr')),
    });
  const dirty = JSON.stringify(draft) !== JSON.stringify(toPatch(room, beds, roomTypes));

  // The panel only grows for a unit that is NOT a plain room. Every hotel room
  // is `unit_kind = 'room'`, so all 42 keep the fiche they have today, field
  // for field — the inertness this increment must not spend (build brief §1).
  //
  // Converting a room into an apartment is deliberately NOT offered: nothing at
  // P1.6 needs it (the apartments are seeded as apartments, and `unit_kind`
  // defaults from the établissement), and a control that restructures a unit's
  // whole bed model is not one to build speculatively (Hard Rule 4).
  // The fiche's noun follows unit_kind rather than being hard-coded « Chambre »,
  // which read « Chambre Apt1 » on an apartment (operator, 2026-08-11).
  // UNIT_KIND_LABELS_FR maps room→Chambre, so all 42 hotel fiches render exactly
  // as before — the inertness claim holds by construction rather than by a
  // ternary that could drift — and studio/villa work unasked.
  const unitKind = draft.unit?.unitKind ?? 'room';
  const apartmentLike = unitKind !== 'room';
  const unit = draft.unit;
  const spaces = draft.beds ?? [];

  const setUnit = (patch: Partial<NonNullable<RoomConfigPatch['unit']>>) =>
    setDraft((d) => ({ ...d, unit: d.unit === null ? null : { ...d.unit, ...patch } }));

  const setSpaces = (next: BedSpace[]) => setDraft((d) => ({ ...d, beds: next }));

  // The XOR expressed once, at the boundary, rather than trusted throughout:
  // an apartment-like unit sends its spaces and no scalar; a plain room sends
  // its scalar and manages NEITHER the unit shape nor the spaces.
  //
  // Both nulls matter, and for the same reason. `beds: []` would issue a delete
  // for 42 rooms that have nothing to delete; `unit: {…}` would write seven
  // columns back to the values they already hold. Either is harmless and both
  // are noise — and sending null instead makes a hotel room's save byte-for-byte
  // the call the pre-P1.6 client made, which is the inertness claim rather than
  // an approximation of it.
  // An apartment-like unit with no space described would save `bedConfig: null`
  // and `beds: []` — the "never neither" half of the XOR, which the database
  // refuses. That refusal is correct but it is the wrong LAYER to meet it in:
  // a unit created through « Nouvelle chambre » in an apartments property lands
  // in exactly this state (the form is hotel-shaped and sets a scalar, which
  // this panel then hides), so the operator would meet a French database error
  // for having opened a fiche. Blocked here instead, with the hint below saying
  // why — form validation, not an exception.
  const incomplete = apartmentLike && spaces.length === 0;

  // The category rides along ONLY for an apartment-like unit, for the same
  // reason `unit` and `beds` do — a hotel room's category is shared (eleven
  // rooms are DSTD), and the RPC refuses to edit a shared one from one fiche.
  // So a hotel save still sends type: null and stays byte-identical.
  //
  // Its capacity is taken from the unit's own « Capacité », not from a second
  // input: for a 1:1 category those are the same fact written twice, and two
  // « Capacité » boxes in one panel is a question nobody should have to answer.
  // Before 0075 they could silently drift, since editing the fiche wrote the
  // unit's figure and left the category's behind.
  const submit = () =>
    onSave({
      ...draft,
      configOptions: apartmentLike ? null : chosen,
      doubleBedType: apartmentLike || !hasDouble ? null : draft.doubleBedType,
      unit: apartmentLike ? draft.unit : null,
      beds: apartmentLike ? spaces : null,
      type:
        apartmentLike && draft.type
          ? { labelFr: draft.type.labelFr, maxOccupancy: draft.maxOccupancy }
          : null,
    });

  return (
    <aside className="pms-sheet" aria-label={`${UNIT_KIND_LABELS_FR[unitKind]} ${room.roomNumber}`}>
      <header className="pms-sheet-head">
        <h3>
          {UNIT_KIND_LABELS_FR[unitKind]} {room.roomNumber}
          <span className="pms-sheet-floor">Étage {room.floor}</span>
        </h3>
        <button type="button" className="pms-sheet-close" onClick={onClose} aria-label="Fermer">
          ✕
        </button>
      </header>

      {!canEdit && (
        <p className="pms-hint">Lecture seule — la modification demande le droit « paramétrage des chambres ».</p>
      )}
      {error && <p className="auth-err">Erreur : {error}</p>}

      <div className="pms-sheet-body">
        <label>
          Catégorie
          <select
            value={draft.roomTypeId === null ? '' : String(draft.roomTypeId)}
            disabled={!canEdit}
            onChange={(e) => set({ roomTypeId: e.target.value === '' ? null : Number(e.target.value) })}
          >
            <option value="">— non typée</option>
            {roomTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} — {t.labelFr}
              </option>
            ))}
          </select>
        </label>

        {/* ⚠ CHECKBOXES, not a dropdown — the room's arrangements are a SET
            (0173). A convertible room used to be expressed by picking the one
            code whose NAME contained both (« Double - Twin »), which is what
            made « Double - Twin » and « Twin - Double » two spellings of one
            room. Ticking two boxes says the same thing without inventing a
            vocabulary for pairs. */}
        {!apartmentLike && (
          <fieldset className="pms-sheet-configs">
            <legend>Configurations possibles</legend>
            {bedConfigs.map((b) => (
              <label key={b.code} className="pms-sheet-config">
                <input
                  type="checkbox"
                  checked={chosen.includes(b.code)}
                  disabled={!canEdit}
                  onChange={() => toggleOption(b.code)}
                />
                {b.code} — {b.labelFr}
              </label>
            ))}
            {chosen.length === 0 && (
              <p className="pms-sheet-warn">
                Une chambre doit pouvoir être faite dans au moins une configuration.
              </p>
            )}
            {/* The bed TYPE is the room's, not the arrangement's — asked only
                when one of the chosen arrangements actually has a double. */}
            {hasDouble && (
              <label className="pms-sheet-bedtype">
                Lit double
                <select
                  value={draft.doubleBedType ?? ''}
                  disabled={!canEdit}
                  onChange={(e) => set({ doubleBedType: e.target.value === '' ? null : e.target.value })}
                >
                  <option value="">—</option>
                  {bedTypes
                    .filter((bt) => bt.sleeps === 2)
                    .map((bt) => (
                      <option key={bt.code} value={bt.code}>
                        {bt.labelFr}
                      </option>
                    ))}
                </select>
              </label>
            )}
            <span className="pms-sheet-preview">
              <BedIcons
                layouts={preview}
                doubleBedType={draft.doubleBedType}
                current={room.currentBedConfig}
              />
            </span>
          </fieldset>
        )}

        <label>
          Capacité (personnes)
          <input
            type="number"
            min={1}
            max={20}
            value={draft.maxOccupancy}
            disabled={!canEdit}
            onChange={(e) => set({ maxOccupancy: Number(e.target.value) })}
          />
        </label>

        <label>
          Vue
          <select
            value={draft.view ?? ''}
            disabled={!canEdit}
            onChange={(e) => set({ view: e.target.value === '' ? null : e.target.value })}
          >
            <option value="">—</option>
            {VIEWS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>

        <label>
          Zone chauffage
          <select
            value={draft.heatingZone === null ? '' : String(draft.heatingZone)}
            disabled={!canEdit}
            onChange={(e) => set({ heatingZone: e.target.value === '' ? null : Number(e.target.value) })}
          >
            <option value="">—</option>
            {ZONES.map((z) => (
              <option key={z} value={z}>
                Zone {z}
              </option>
            ))}
          </select>
        </label>

        <label>
          Zone chauffage SdB
          <select
            value={draft.bathroomHeatingZone === null ? '' : String(draft.bathroomHeatingZone)}
            disabled={!canEdit}
            onChange={(e) =>
              set({ bathroomHeatingZone: e.target.value === '' ? null : Number(e.target.value) })
            }
          >
            <option value="">— (comme la chambre)</option>
            {ZONES.map((z) => (
              <option key={z} value={z}>
                Zone {z}
              </option>
            ))}
          </select>
        </label>

        <div className="pms-sheet-flags">
          <label className="pms-sheet-check">
            <input
              type="checkbox"
              checked={draft.isSuperior}
              disabled={!canEdit}
              onChange={(e) => set({ isSuperior: e.target.checked })}
            />
            Supérieure (balcon)
          </label>
          <label className="pms-sheet-check">
            <input
              type="checkbox"
              checked={draft.hasShower}
              disabled={!canEdit}
              onChange={(e) => set({ hasShower: e.target.checked })}
            />
            Douche (sinon baignoire)
          </label>
          <label className="pms-sheet-check">
            <input
              type="checkbox"
              checked={draft.wcNotSeparated}
              disabled={!canEdit}
              onChange={(e) => set({ wcNotSeparated: e.target.checked })}
            />
            WC non séparé
          </label>
        </div>

        {apartmentLike && draft.type !== null && (
          <>
            <label className="pms-sheet-wide">
              Libellé catégorie
              <input
                type="text"
                value={draft.type.labelFr}
                disabled={!canEdit}
                onChange={(e) =>
                  setDraft((d) => (d.type === null ? d : { ...d, type: { ...d.type, labelFr: e.target.value } }))
                }
              />
            </label>
            {/* Outside the <label> deliberately: nested in it, this whole
                sentence became part of the field's accessible name, so a screen
                reader would announce the hint as the label. */}
            <p className="pms-hint pms-sheet-wide">
              Ce logement est sa propre catégorie — le libellé se modifie ici. Le code (
              {roomTypes.find((t) => t.id === draft.roomTypeId)?.code ?? '—'}) reste fixe, et la capacité de la
              catégorie suit « Capacité » ci-dessus.
            </p>
          </>
        )}

        {apartmentLike && unit !== null && (
          <>
            <p className="pms-sheet-section">Composition — {UNIT_KIND_LABELS_FR[unit.unitKind]}</p>
            <UnitScalars unit={unit} disabled={!canEdit} onChange={setUnit} />
            <SpaceEditor
              spaces={spaces}
              bedConfigs={bedConfigs}
              maxOccupancy={draft.maxOccupancy}
              disabled={!canEdit}
              onChange={setSpaces}
            />
          </>
        )}

        <label className="pms-sheet-wide">
          Remarques
          <textarea
            rows={2}
            value={draft.remarks ?? ''}
            disabled={!canEdit}
            placeholder="PMR, Petite, Vue restreinte…"
            onChange={(e) => set({ remarks: e.target.value })}
          />
        </label>
      </div>

      <footer className="pms-sheet-foot">
        <span className="pms-sheet-trace">
          {room.updatedAt
            ? `Modifié le ${fmtDate(room.updatedAt.slice(0, 10))}`
            : 'Jamais modifié depuis cette page'}
          {canEdit && (
            <>
              {' · '}
              {confirmDelete ? (
                <button type="button" className="pms-link is-danger" onClick={onDelete} disabled={saving}>
                  Confirmer la suppression
                </button>
              ) : (
                <button type="button" className="pms-link" onClick={() => setConfirmDelete(true)}>
                  Supprimer
                </button>
              )}
            </>
          )}
        </span>
        <div className="pms-sheet-actions">
          <button type="button" className="auth-btn is-ghost" onClick={onClose} disabled={saving}>
            {canEdit ? 'Annuler' : 'Fermer'}
          </button>
          {canEdit && (
            <button
              type="button"
              className="auth-btn"
              disabled={!dirty || saving || incomplete}
              onClick={submit}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          )}
        </div>
      </footer>
    </aside>
  );
}
